import type { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import Header from '../../../components/Header/Header';
import axios from 'axios';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import SEO from '../../../components/SEO';
import { ProductSchema, BreadcrumbSchema } from '../../../components/StructuredData';
import { getProductKeywords, formatKeywords } from '../../../data/keywords';
import { getProductIdBySlug } from '../../../data/products';

// Lazy load components below the fold for better initial page load
const BookPageHome = dynamic(() => import('../../../components/bookpage/BookPageHome'), {
  loading: () => <div style={{ minHeight: '600px' }} />,
});

const DrivingInnovation = dynamic(() => import('../../../components/bookpage/DrivingInnovation'), {
  loading: () => <div style={{ minHeight: '400px' }} />,
});

const LicensingOptions = dynamic(() => import('../../../components/bookpage/LicensingOptions'), {
  loading: () => <div style={{ minHeight: '300px' }} />,
});

const KeyMain = dynamic(() => import('../../../components/bookpage/Keyfeature'), {
  loading: () => <div style={{ minHeight: '400px' }} />,
});

const DevelopersChoose = dynamic(() => import('../../../components/bookpage/DevelopersChoose'), {
  loading: () => <div style={{ minHeight: '300px' }} />,
});

const HowWork = dynamic(() => import('../../../components/bookpage/HowWork'), {
  loading: () => <div style={{ minHeight: '400px' }} />,
});

const ProductFAQ = dynamic(() => import('../../../components/bookpage/ProductFAQ'), {
  loading: () => <div style={{ minHeight: '300px' }} />,
});

const Footer = dynamic(() => import('../../../components/Footer/Footer'));

interface ProductData {
  [key: string]: any;
}

interface FaqItem {
  _id: string;
  question: string;
  answer: string;
  category: string;
}

interface BookPageProps {
  productData: ProductData;
  aboutData: any[];
  categories: string[];
  faqs: FaqItem[];
}

const BookPage: NextPage<BookPageProps> = ({ productData, aboutData, categories, faqs }) => {
  const router = useRouter();

  useEffect(() => {
    if (!router.isReady) return;

    const scrollTarget = router.query.scroll as string;

    if (!scrollTarget) return;

    const performScroll = () => {
      const element = document.getElementById(scrollTarget);
      if (element) {
        const offset = 165;
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
        return true;
      }
      return false;
    };

    // Try immediately
    if (performScroll()) return;

    // Retry mechanism - element might not be rendered yet
    let retries = 0;
    const maxRetries = 20;
    const retryInterval = 200;

    const tryScroll = () => {
      if (performScroll()) return;

      if (retries < maxRetries) {
        retries++;
        setTimeout(tryScroll, retryInterval);
      }
    };

    setTimeout(tryScroll, retryInterval);
  }, [router.isReady, router.query.scroll]);

  const siteUrl = process.env.SITE_URL || 'https://vxfx.io';
  const productImage = productData?.image?.url || `${siteUrl}/epenfav.webp`;

  // Create optimized description with call-to-action
  let productDescription = productData?.description || productData?.name || '';
  if (productDescription && productDescription.length > 120) {
    const truncated = productDescription.substring(0, 120).trim();
    const lastSpace = truncated.lastIndexOf(' ');
    productDescription =
      lastSpace > 100
        ? `${truncated.substring(0, lastSpace)}... Discover more!`
        : `${truncated}... Get started today!`;
  } else if (productDescription && productDescription.length < 100) {
    productDescription = `${productDescription} Professional video plugin solution. Explore features and pricing now!`;
  }

  // Get optimized keywords for the product
  const productKeywords = productData?.name
    ? getProductKeywords(productData.name, productData?.category)
    : null;
  const keywordsString = productKeywords ? formatKeywords(productKeywords) : undefined;

  return (
    <>
      <SEO
        title={productData?.name || 'Product'}
        description={productDescription}
        image={productImage}
        type="product"
        keywords={keywordsString}
      />
      <ProductSchema
        name={productData?.name || 'Product'}
        description={productDescription}
        image={productImage}
        url={`${siteUrl}/video-plugin-framework/${router.query.url}`}
        brand="EPEN Ltd."
        sku={productData?._id}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Products', url: '/video-plugin-framework' },
          { name: productData?.name || 'Product', url: `/video-plugin-framework/${router.query.url}` },
        ]}
      />
      <Header />
      <section>
        <BookPageHome data={productData} />
      </section>

      <DrivingInnovation />

      <section>
        <LicensingOptions />
      </section>

      <section>
        <KeyMain data={productData} />
      </section>

      <section>
        <DevelopersChoose />
      </section>

      <section>
        <HowWork data={productData} />
      </section>

      <ProductFAQ categories={categories} faqs={faqs} />

      <Footer aboutData={aboutData} />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { url } = context.query;
  const slug = url as string;
  let productData = null;
  let categories: string[] = [];
  let faqs: FaqItem[] = [];
  let aboutData = [];

  try {
    // Get product ID from slug
    const productId = getProductIdBySlug(slug);
    if (!productId) {
      return {
        notFound: true,
      };
    }

    const response = await axios.get(
      `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/product-details/${productId}`
    );
    productData = response.data.data;

    const faqsResponse = await axios.get(`${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/faqs`);
    categories = faqsResponse.data.data.categories || [];
    faqs = faqsResponse.data.data.faqs || [];

    const aboutResponse = await axios.get(
      `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/about-us`
    );
    aboutData = aboutResponse.data.data || [];
  } catch (err) {
    console.error(`Error fetching product/faqs/about-us data for slug "${slug}":`, err);
  }

  return {
    props: {
      productData: productData || {},
      categories: categories || [],
      faqs: faqs || [],
      aboutData: aboutData,
    },
  };
};

export default BookPage;
